import { h, Fragment } from 'preact';
import { useEffect, useState, useRef } from 'preact/hooks';
import { useSelector } from 'react-redux';
import ReactPlayerLoader from '@brightcove/react-player-loader';
import { MQC_BANNER } from 'common/constants/images-mqc';
import { checkIsIOS } from 'common/helpers/misc';
import { dataLayerPush } from 'common/helpers/datalayer-push';
import VideoChapters from 'common/components/video-chapters';
import LazyImage from 'common/components/lazy-image/lazy-image';
import WatchNext from './WatchNext';
import VideoInformation from './VideoInformation';
import VideoTitleAndPlayButton from './VideoTitleAndPlayButton';
import * as Styled from '../styles/styles';

export default function VideoPlayer({ data }) {
    const {
        video,
        metadata,
        watchNext,
        inline = false,
        inlinePlay,
        videoChapters,
        account,
        thumbnail,
        seriesLogo
    } = data;
    const isDesktop = useSelector( state => state.ui.isDesktop );
    const isDesktopSmall = useSelector( state => state.ui.isDesktopSmall );
    const accessibilityOptions = useSelector(state => state.accessibilityOptions);

    const playerId = data.player;
    const videoElement = useRef(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [progress, setProgress] = useState(null);
    const [playing, setPlaying] = useState(false);
    const [ended, setEnded] = useState(false);
    const [player, setPlayer] = useState(null);
    const [playerReady, setPlayerReady] = useState(false);
    const [playerInitialised, setPlayerInitialised] = useState(false);
    const [isIOS, setIsIOS] = useState(false);

    useEffect(() => {
        dataLayerPush('video', video.id);
    }, [video.id]);

    useEffect(() => {
        if (playing) {
            setTimeout(() => {
                setCurrentTime(player.currentTime());
            }, 1000);
        }
    }, [playing, currentTime]);

    useEffect(() => {
        if (player && playerReady) {
            // set Audio description on or off
            const audioKind = accessibilityOptions.audioDescription ? 'Descriptive' : 'Main';
            const audioTracks = player.audioTracks();

            // If more than 90% the player is outside of the viewport, activate PIP mode.
            player.pip({viewable: 0.9});

            for (let i = 0; i < audioTracks.length; i++) {
                audioTracks[i].enabled = audioTracks[i].label.includes(audioKind);
            }

            // set subtitles on or off
            const browserLanguage = navigator.language.slice(0, 2);
            const subtitlesKind = accessibilityOptions.subtitles ? 'showing' : 'disabled';
            const textTracks = player.textTracks();

            for (let i = 0; i < textTracks.length; i++) {
                const trackLanguage = textTracks[i].language.slice(0, 2);
                if (textTracks[i].kind === 'subtitles' && (browserLanguage === trackLanguage)) {
                    textTracks[i].mode = subtitlesKind;
                }
            }
        }
    }, [player, accessibilityOptions, playerReady]);

    useEffect(() => {
        setIsIOS(checkIsIOS(window.navigator));
    }, []);

    useEffect(() => {
        setPlaying(false);
        const watchProgress = parseInt(localStorage.getItem(`video-progress-${video.id}`));

        if (Number.isNaN(watchProgress)) {
            return setProgress(0);
        }
        
        setProgress(watchProgress);
    }, [video.id]);

    useEffect(() => {

        /* 
            For non-iOS devices, update video progress based on saved state in localStorage from previous session
            iOS restriction is due to a Brightcove bug where player.currentTime() causes unpredictable responses
        */
        if (!isIOS && player && progress && playerReady && !playerInitialised) {
            // set the video to the user's watch progress time (or if completed, reset to beginning)
            const time = progress === 100 ? 0 : Math.floor(player.duration() * (progress / 100));
            player.currentTime(time);
            setPlayerInitialised(true);
        }

    }, [inlinePlay, player, playerReady, playerInitialised, progress]);

    useEffect(() => {
        if (player) {
            if (inlinePlay) {
                player.play();
            } else {
                player.pause();
            }
        }
    }, [inlinePlay, player]);

    if (!video?.id || !video?.mediaId || isDesktop === null) {
        return '';
    }

    /**
     * Callback function when brightcove script has been loaded
     * @param {Object} BC player reference
     */
    const handleSuccess = ({ ref }) => {
        // ref = BC player object
        ref.on('play', onPlay.bind(this, ref));
        ref.on('pause', onPause.bind(this, ref));
        ref.on('ended', onEnded.bind(this, ref));
        ref.on('loadedmetadata', onMetadata.bind(this, ref));
        ref.on('dispose', onDispose.bind(this, ref));

        if (inline) {
            ref.player().controlBar.hide();
        }

        setPlayer(ref);
    };

    const onPlay = player => {
        if ( player ) {
            const watchProgress = Math.round( ( 100 / player.duration() ) * player.currentTime() ).toString();
            localStorage.setItem( `video-progress-${ video.id }`, watchProgress );
            setProgress( watchProgress );
            setCurrentTime(player.currentTime());
        }
        setEnded(false);
        setPlaying(true);
    };

    const onPause = player => {
        if ( player ) {
            const watchProgress = Math.round( ( 100 / player.duration() ) * player.currentTime() ).toString();
            localStorage.setItem( `video-progress-${ video.id }`, watchProgress );
            setProgress( watchProgress );
        }
        setPlaying(false);
    };

    const onEnded = player => {
        localStorage.setItem(`video-progress-${video.id}`, '100');
        setProgress(progress);
        setPlaying(false);
        setEnded(true);
        if (player.isFullscreen()) {
            player.exitFullscreen();
        }
    };

    const onMetadata = () => {
        setPlayerReady(true);
        setEnded(false);
    };

    const onDispose = () => {
        if (localStorage.getItem(`video-progress-${video.id}`) && player) {
            const watchProgress = Math.round( ( 100 / player.duration() ) * player.currentTime() ).toString();
            localStorage.setItem(`video-progress-${video.id}`, watchProgress);
        }
    };

    const handleVideoAction = () => {
        if (playing) {
            player.pause();
        } else {
            player.play();
            player.playlistEndscreenModal?.close?.();
        }
    };

    const gotoTimeStamp = (time) => {
        if (!playing) {
            player.play();
        }

        if(videoElement.current) {
            videoElement.current.player.currentTime(time);
            setCurrentTime(time);
        }
        return;
    };

    return (
        <>
            <Styled.VideoPlayerWidget inline={inline} className={(inline && !inlinePlay) && 'is-hidden'} ended={ended}>
                {(!inline && thumbnail) && (
                    <Styled.ImageContainer isPlaying={playing}>
                        <LazyImage image={ thumbnail } mediaQueryConfig={ MQC_BANNER } />
                    </Styled.ImageContainer>
                )}

                {video?.mediaId && <ReactPlayerLoader
                    ref={videoElement}
                    accountId={account}
                    playerId={playerId}
                    videoId={video.mediaId}
                    options={{ playsinline: true }}
                    attrs={{ className: inline ? 'vjs-container' : 'vjs-pip-container' }}
                    poster={false}
                    onSuccess={handleSuccess}
                />}

                {ended && <WatchNext videos={watchNext} />}

                {(!inline && !isDesktopSmall && seriesLogo) && (
                    <VideoTitleAndPlayButton
                        accessibilityOptions={accessibilityOptions}
                        videoId={video.id}
                        title={video.title}
                        metadata={metadata}
                        seriesLogo={seriesLogo}
                        playing={playing}
                        ended={ended}
                        handleVideoAction={handleVideoAction}
                    />
                )}
                {(!inline && isDesktopSmall) && (
                    <VideoInformation
                        isDesktopSmall={isDesktopSmall}
                        accessibilityOptions={accessibilityOptions}
                        video={video}
                        metadata={metadata}
                        playing={playing}
                        ended={ended}
                        seriesLogo={seriesLogo}

                        handleVideoAction={handleVideoAction} />
                )}
            </Styled.VideoPlayerWidget>

            {(!inline && !isDesktopSmall) && (
                <VideoInformation
                    isDesktopSmall={isDesktopSmall}
                    accessibilityOptions={accessibilityOptions}
                    video={video}
                    metadata={metadata}
                    playing={playing}
                    ended={ended}
                    seriesLogo={seriesLogo}

                    handleVideoAction={handleVideoAction}
                />
            )}
            { videoChapters && (
                <Styled.InfoContainer>
                    <VideoChapters 
                        videoChapters={videoChapters}
                        accessibilityOptions={accessibilityOptions}
                        currentTime={currentTime}
                        handleClick={gotoTimeStamp} 
                    />
                </Styled.InfoContainer>
            )}
        </>
    );
}
